import React from 'react';
import { RotateCw, RotateCcw, FlipHorizontal, FlipVertical, Sun, Contrast, Droplets, Palette } from 'lucide-react';
import { Slider } from '../ui/slider';
import { Button } from '../ui/button';

const FILTER_PRESETS = [
  { name: 'Original', filters: { brightness: 100, contrast: 100, saturate: 100, grayscale: 0, sepia: 0 } },
  { name: 'Warm', filters: { brightness: 105, contrast: 105, saturate: 120, grayscale: 0, sepia: 15 } },
  { name: 'Cool', filters: { brightness: 100, contrast: 110, saturate: 90, grayscale: 0, sepia: 0 } },
  { name: 'Vivid', filters: { brightness: 105, contrast: 120, saturate: 140, grayscale: 0, sepia: 0 } },
  { name: 'Muted', filters: { brightness: 100, contrast: 90, saturate: 60, grayscale: 0, sepia: 10 } },
  { name: 'B&W', filters: { brightness: 105, contrast: 115, saturate: 0, grayscale: 100, sepia: 0 } },
  { name: 'Vintage', filters: { brightness: 95, contrast: 90, saturate: 80, grayscale: 0, sepia: 30 } },
  { name: 'Dramatic', filters: { brightness: 90, contrast: 140, saturate: 110, grayscale: 0, sepia: 0 } },
];

export function PhotoEditor({ filters, onFiltersChange, onRotate, onFlip }) {
  const updateFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-5" data-testid="photo-editor-controls">
      {/* Rotate / Flip */}
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">Transform</label>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onRotate(-90)} className="flex-1 border-[#e2e8f0]" data-testid="rotate-left-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Left
          </Button>
          <Button variant="outline" size="sm" onClick={() => onRotate(90)} className="flex-1 border-[#e2e8f0]" data-testid="rotate-right-btn">
            <RotateCw className="w-4 h-4 mr-1" /> Right
          </Button>
          <Button variant="outline" size="sm" onClick={() => onFlip('h')} className="flex-1 border-[#e2e8f0]" data-testid="flip-h-btn">
            <FlipHorizontal className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onFlip('v')} className="flex-1 border-[#e2e8f0]" data-testid="flip-v-btn">
            <FlipVertical className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filter Presets */}
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">Presets</label>
        <div className="grid grid-cols-4 gap-1.5">
          {FILTER_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => onFiltersChange(preset.filters)}
              className={`px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                JSON.stringify(filters) === JSON.stringify(preset.filters)
                  ? 'border-[#2563eb] bg-[#eff6ff] text-[#2563eb]'
                  : 'border-[#e2e8f0] text-[#475569] hover:border-[#2563eb]/50'
              }`}
              data-testid={`preset-${preset.name.toLowerCase()}`}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {/* Manual Adjustments */}
      <div className="space-y-4">
        <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] block">Adjustments</label>
        
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-[#475569] flex items-center gap-1"><Sun className="w-3 h-3" /> Brightness</span>
            <span className="text-xs text-[#94a3b8]">{filters.brightness || 100}%</span>
          </div>
          <Slider
            value={[filters.brightness || 100]}
            onValueChange={([v]) => updateFilter('brightness', v)}
            min={20} max={200} step={1}
            className="w-full"
            data-testid="brightness-slider"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-[#475569] flex items-center gap-1"><Contrast className="w-3 h-3" /> Contrast</span>
            <span className="text-xs text-[#94a3b8]">{filters.contrast || 100}%</span>
          </div>
          <Slider
            value={[filters.contrast || 100]}
            onValueChange={([v]) => updateFilter('contrast', v)}
            min={20} max={200} step={1}
            className="w-full"
            data-testid="contrast-slider"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-[#475569] flex items-center gap-1"><Droplets className="w-3 h-3" /> Saturation</span>
            <span className="text-xs text-[#94a3b8]">{filters.saturate || 100}%</span>
          </div>
          <Slider
            value={[filters.saturate || 100]}
            onValueChange={([v]) => updateFilter('saturate', v)}
            min={0} max={200} step={1}
            className="w-full"
            data-testid="saturation-slider"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-[#475569] flex items-center gap-1"><Palette className="w-3 h-3" /> Sepia</span>
            <span className="text-xs text-[#94a3b8]">{filters.sepia || 0}%</span>
          </div>
          <Slider
            value={[filters.sepia || 0]}
            onValueChange={([v]) => updateFilter('sepia', v)}
            min={0} max={100} step={1}
            className="w-full"
            data-testid="sepia-slider"
          />
        </div>
      </div>
    </div>
  );
}

export { FILTER_PRESETS };
